import type { OrderQuote, SaveOrderQuoteRequest } from '@avrahami/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CloseIcon,
  formatDateTime,
  Loading,
  PlusIcon,
  saveFile,
  TextAreaField,
  TextField,
  uiWords,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type InputHTMLAttributes } from 'react';
import { keys, orderQuoteQuery, quoteProductsQuery, tenantApi } from '../api/tenant';
import { he } from '../i18n/he';
import { errorMessage, mergeErrors, NO_ERRORS, type FieldErrors } from '../lib/forms';
import { formatMoney } from '../lib/money';
import {
  customLine,
  lineErrors,
  lineTotal,
  productLine,
  quoteDraft,
  quoteRequest,
  quoteTotals,
  type QuoteDraft,
  type QuoteLineDraft,
} from '../lib/quote';
import { FormStatus } from './fields';

/**
 * The store's quote for a request (docs/TENANT_ADMIN.md "The quote"): its lines, prices and
 * terms, edited here and downloaded as a PDF to send to the shopper. The request itself stays as
 * the shopper sent it.
 */
export function QuoteCard({
  orderId,
  number,
  canEdit,
  timeZone,
}: {
  orderId: string;
  number: number;
  canEdit: boolean;
  timeZone: string;
}) {
  const quote = useQuery(orderQuoteQuery(orderId));
  return (
    <Card title={he.quote.title}>
      {quote.isPending ? <Loading /> : null}
      {quote.isError ? <Alert>{errorMessage(quote.error)}</Alert> : null}
      {quote.data === undefined ? null : (
        <QuoteEditor
          orderId={orderId}
          number={number}
          canEdit={canEdit}
          timeZone={timeZone}
          quote={quote.data}
          onReload={() => quote.refetch()}
        />
      )}
    </Card>
  );
}

/** A compact input of a quote line; the column's name is its accessible label. */
function LineInput({
  label,
  error,
  className = '',
  ...rest
}: { label: string; error?: string | undefined } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="text-xs text-slate-500 sm:sr-only">{label}</span>
      <input
        {...rest}
        aria-invalid={error !== undefined}
        className={`w-full min-w-0 rounded-md border px-2 py-1.5 text-sm shadow-sm focus:outline-2 focus:outline-brand-600 disabled:bg-slate-50 ${error === undefined ? 'border-slate-300' : 'border-red-500'}`}
      />
      {error === undefined ? null : <span className="text-xs text-red-700">{error}</span>}
    </label>
  );
}

const LINE_GRID =
  'grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_8rem_5rem_8rem_7rem_2rem] sm:items-start';

function QuoteEditor({
  orderId,
  number,
  canEdit,
  timeZone,
  quote,
  onReload,
}: {
  orderId: string;
  number: number;
  canEdit: boolean;
  timeZone: string;
  quote: OrderQuote;
  onReload: () => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<QuoteDraft>(() => quoteDraft(quote));
  const [version, setVersion] = useState(quote.version);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>(NO_ERRORS);
  const [picking, setPicking] = useState(false);

  const change = (next: (current: QuoteDraft) => QuoteDraft) => {
    setDraft(next);
    setDirty(true);
    save.reset();
  };
  const changeLine = (key: string, fields: Partial<QuoteLineDraft>) => {
    change((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...fields } : line)),
    }));
  };
  const restart = (from: OrderQuote) => {
    setDraft(quoteDraft(from));
    setVersion(from.version);
    setDirty(false);
    setErrors(NO_ERRORS);
  };
  if (!dirty && quote.version !== version) {
    // Someone else saved meanwhile and nothing is being edited here: follow the server (React's
    // documented pattern of adjusting state during render).
    restart(quote);
  }

  const save = useMutation({
    mutationFn: (body: SaveOrderQuoteRequest) => tenantApi.saveOrderQuote(orderId, body),
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.orderQuote(orderId), saved);
      restart(saved);
    },
    onError: (error) => {
      setErrors(mergeErrors(NO_ERRORS, error));
    },
  });
  const download = useMutation({
    mutationFn: () => tenantApi.orderQuoteDocument(orderId),
    onSuccess: (pdf) => {
      saveFile(pdf, `quote-${String(number)}.pdf`);
    },
  });

  const submit = () => {
    const parsed = quoteRequest(draft, version);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };

  const totals = quoteTotals(draft, quote.vatRatePercent);
  const localInvalid = Object.keys(errors).length > 0 && !save.isError;
  const disabled = !canEdit || save.isPending;

  return (
    <div className="flex flex-col gap-4" data-testid="quote-editor">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {quote.saved && quote.updatedAt !== null ? (
          <span className="text-slate-600" data-testid="quote-saved-at">
            {he.quote.savedAt} {formatDateTime(quote.updatedAt, timeZone)}
            {quote.updatedBy === null ? null : ` ${he.quote.savedBy} ${quote.updatedBy}`}
          </span>
        ) : (
          <Badge tone="amber">{he.quote.draft}</Badge>
        )}
      </div>
      <p className="text-sm text-slate-600">{canEdit ? he.quote.intro : he.quote.readOnly}</p>

      <div className="flex flex-col gap-3">
        <div
          className={`${LINE_GRID} hidden border-b border-slate-200 pb-2 text-xs text-slate-500 sm:grid`}
        >
          <span>{he.quote.product}</span>
          <span>{he.quote.sku}</span>
          <span>{he.quote.quantity}</span>
          <span>{he.quote.unitPrice}</span>
          <span>{he.quote.lineTotal}</span>
          <span />
        </div>
        {draft.lines.map((line, index) => {
          const lineError = lineErrors(errors, index);
          const total = lineTotal(line);
          const own = line.productId === null;
          return (
            <div
              key={line.key}
              className={`${LINE_GRID} border-b border-slate-100 pb-3`}
              data-testid="quote-line"
            >
              {own ? (
                <LineInput
                  label={he.quote.product}
                  className="col-span-2 sm:col-span-1"
                  value={line.title}
                  placeholder={he.quote.titlePlaceholder}
                  maxLength={300}
                  disabled={disabled}
                  error={lineError.title}
                  data-testid="quote-line-title"
                  onChange={(event) => {
                    changeLine(line.key, { title: event.target.value });
                  }}
                />
              ) : (
                <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                  <span className="text-sm" data-testid="quote-line-product">
                    {line.title}
                  </span>
                  {lineError.product === undefined ? null : (
                    <span className="text-xs text-red-700">{lineError.product}</span>
                  )}
                </div>
              )}
              {own ? (
                <LineInput
                  label={he.quote.sku}
                  value={line.sku}
                  dir="ltr"
                  maxLength={50}
                  disabled={disabled}
                  error={lineError.sku}
                  onChange={(event) => {
                    changeLine(line.key, { sku: event.target.value });
                  }}
                />
              ) : (
                <span className="ltr self-center text-sm text-slate-600 sm:self-start sm:pt-1.5">
                  {line.sku}
                </span>
              )}
              <LineInput
                label={he.quote.quantity}
                value={line.quantity}
                inputMode="numeric"
                dir="ltr"
                disabled={disabled}
                error={lineError.quantity}
                data-testid="quote-line-quantity"
                onChange={(event) => {
                  changeLine(line.key, { quantity: event.target.value });
                }}
              />
              <LineInput
                label={he.quote.unitPrice}
                value={line.price}
                inputMode="decimal"
                dir="ltr"
                disabled={disabled}
                error={lineError.price}
                data-testid="quote-line-price"
                onChange={(event) => {
                  changeLine(line.key, { price: event.target.value });
                }}
              />
              <span
                className="self-center whitespace-nowrap text-sm font-medium sm:self-start sm:pt-1.5"
                data-testid="quote-line-total"
              >
                <span className="text-xs font-normal text-slate-500 sm:hidden">
                  {he.quote.lineTotal}:{' '}
                </span>
                {total === null ? he.quote.noPrice : formatMoney(total)}
              </span>
              {canEdit ? (
                <button
                  type="button"
                  aria-label={`${he.quote.removeLine}: ${line.title || String(index + 1)}`}
                  title={he.quote.removeLine}
                  disabled={disabled}
                  className="justify-self-end rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 sm:justify-self-auto"
                  data-testid="quote-line-remove"
                  onClick={() => {
                    change((current) => ({
                      ...current,
                      lines: current.lines.filter((other) => other.key !== line.key),
                    }));
                  }}
                >
                  <CloseIcon />
                </button>
              ) : (
                <span />
              )}
            </div>
          );
        })}
        {errors['lines'] === undefined ? null : <Alert>{errors['lines']}</Alert>}
      </div>

      {canEdit ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              tone="secondary"
              disabled={disabled}
              data-testid="quote-add-product"
              onClick={() => {
                setPicking((open) => !open);
              }}
            >
              <span className="inline-flex items-center gap-1">
                <PlusIcon />
                {he.quote.addProduct}
              </span>
            </Button>
            <Button
              tone="secondary"
              disabled={disabled}
              data-testid="quote-add-custom"
              onClick={() => {
                change((current) => ({ ...current, lines: [...current.lines, customLine()] }));
              }}
            >
              <span className="inline-flex items-center gap-1">
                <PlusIcon />
                {he.quote.addCustom}
              </span>
            </Button>
          </div>
          {picking ? (
            <ProductPicker
              onPick={(line) => {
                change((current) => ({ ...current, lines: [...current.lines, line] }));
                setPicking(false);
              }}
              onClose={() => {
                setPicking(false);
              }}
            />
          ) : null}
        </div>
      ) : null}

      <dl
        className="ms-auto grid w-full max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm"
        data-testid="quote-totals"
      >
        <dt className="text-slate-600">{he.quote.beforeVat}</dt>
        <dd>{formatMoney(totals.beforeVatMinor)}</dd>
        <dt className="text-slate-600">
          {he.quote.vat} ({quote.vatRatePercent}%)
        </dt>
        <dd>{formatMoney(totals.vatMinor)}</dd>
        <dt className="font-semibold">{he.quote.total}</dt>
        <dd className="font-semibold" data-testid="quote-total">
          {formatMoney(totals.totalMinor)}
        </dd>
      </dl>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label={he.quote.validUntil}
          type="date"
          name="validUntil"
          value={draft.validUntil}
          disabled={disabled}
          error={errors['validUntil']}
          onChange={(event) => {
            const validUntil = event.target.value;
            change((current) => ({ ...current, validUntil }));
          }}
        />
        <TextField
          label={he.quote.paymentTerms}
          name="paymentTerms"
          value={draft.paymentTerms}
          placeholder={he.quote.paymentTermsPlaceholder}
          maxLength={500}
          disabled={disabled}
          error={errors['paymentTerms']}
          onChange={(event) => {
            const paymentTerms = event.target.value;
            change((current) => ({ ...current, paymentTerms }));
          }}
        />
        <TextAreaField
          label={he.quote.notes}
          name="notes"
          className="sm:col-span-2"
          rows={3}
          value={draft.notes}
          maxLength={2000}
          hint={he.quote.notesHint}
          disabled={disabled}
          error={errors['notes']}
          onChange={(event) => {
            const notes = event.target.value;
            change((current) => ({ ...current, notes }));
          }}
        />
      </div>

      <FormStatus
        error={save.error}
        localInvalid={localInvalid}
        saved={save.isSuccess}
        onReload={() => {
          void onReload().then(() => {
            const latest = queryClient.getQueryData<OrderQuote>(keys.orderQuote(orderId));
            if (latest !== undefined) restart(latest);
            save.reset();
          });
        }}
      />

      <div className="flex flex-wrap items-center gap-2">
        {canEdit ? (
          <Button
            busy={save.isPending}
            disabled={!dirty && quote.saved}
            data-testid="quote-save"
            onClick={submit}
          >
            {he.quote.save}
          </Button>
        ) : null}
        <Button
          tone="secondary"
          disabled={dirty || download.isPending}
          data-testid="quote-download"
          onClick={() => {
            download.mutate();
          }}
        >
          {download.isPending ? uiWords.loading : he.quote.download}
        </Button>
        {dirty ? (
          <>
            <Button
              tone="secondary"
              disabled={save.isPending}
              data-testid="quote-discard"
              onClick={() => {
                restart(quote);
                save.reset();
              }}
            >
              {he.quote.discard}
            </Button>
            <span className="text-xs text-slate-500">{he.quote.saveFirst}</span>
          </>
        ) : null}
      </div>
      {download.isError ? <Alert>{errorMessage(download.error)}</Alert> : null}
    </div>
  );
}

const SEARCH_DELAY_MS = 300;

/** Finds a product of the store by name or SKU and adds it as a line. */
function ProductPicker({
  onPick,
  onClose,
}: {
  onPick: (line: QuoteLineDraft) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  // Searches once typing pauses, not on every key.
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(text.trim());
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [text]);
  const products = useQuery(quoteProductsQuery(q));
  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-slate-200 p-3"
      data-testid="quote-picker"
    >
      <div className="flex items-end gap-2">
        <TextField
          label={he.quote.searchProducts}
          className="flex-1"
          value={text}
          autoFocus
          autoComplete="off"
          data-testid="quote-picker-search"
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        <button
          type="button"
          aria-label={he.quote.closeSearch}
          title={he.quote.closeSearch}
          className="mb-1 rounded p-1.5 text-slate-500 hover:bg-slate-100"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </div>
      {q === '' ? null : products.isError ? (
        <Alert>{errorMessage(products.error)}</Alert>
      ) : products.data === undefined ? (
        <p className="text-sm text-slate-500">{he.quote.searching}</p>
      ) : products.data.items.length === 0 ? (
        <p className="text-sm text-slate-500">{he.quote.noProducts}</p>
      ) : (
        <ul className="flex max-h-64 flex-col divide-y divide-slate-100 overflow-y-auto">
          {products.data.items.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                className="flex w-full flex-wrap items-center justify-between gap-2 px-2 py-2 text-start text-sm hover:bg-slate-50"
                data-testid="quote-picker-result"
                onClick={() => {
                  onPick(productLine(product));
                }}
              >
                <span className="min-w-0 flex-1">
                  {product.title ?? product.name}
                  <span className="ltr ms-2 text-xs text-slate-500">{product.sku}</span>
                </span>
                <span className="whitespace-nowrap text-slate-700">
                  {product.amountMinor === null
                    ? he.quote.noPrice
                    : formatMoney(product.amountMinor)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
